"use client";

import Button, { BtnType } from "@/components/common/buttons/Button";
import InputTextfield from "@/components/common/inputs/InputTextfield";
import Heading2 from "@/components/common/text/Heading2";
import { APP_ROUTES } from "@/constants/routes";
import { clearErrorMess, setSuccessMess } from "@/lib/features/statusSlice";
import { useAppDispatch } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import UserServicese from "@/services/main/UserServices";
import { forgotPasswordSchema } from "@/utils/validation";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useForm } from "react-hook-form";
import classes from "./styles.module.scss";

interface FormValues {
  email: string;
}

export default function Login() {
  const dispatch = useAppDispatch();
  const locale = useLocale();
  const t = useTranslations("messages");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(forgotPasswordSchema),
    mode: "onChange",
  });

  const forgotPasswordMutation = useMutation({
    mutationKey: ["forgotPassword"],
    mutationFn: async (data: FormValues) => {
      return await UserServicese.sendEmailForgotPassword(data.email);
    },
    onSuccess: (res) => {
      dispatch(setSuccessMess(t("forgot_password_send_success")));
    },
    onError: (error: any) => {
      dispatch(clearErrorMess(t("auth_user_not_found")));
    },
  });

  const onSubmit = (data: FormValues) => {
    forgotPasswordMutation.mutate(data);
  };

  return (
    <Box className="flex flex-col w-full h-full">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Heading2
          $colorName="--cimigo-blue"
          translation-key="forgot_password_title"
        >
          {t("forgot_password_title")}
        </Heading2>

        <p
          className="mt-2 mb-0 text-sm leading-[24px] tracking-[0.25px] text-gray-80"
          translation-key="forgot_password_sub_title"
        >
          {t("forgot_password_sub_title")}
        </p>
        <Box className="flex flex-col mt-8">
          <Box>
            <Box>
              <InputTextfield
                transiton-key="field_email_address"
                transiton-key-placeholder="field_business_email_placeholder"
                title={t("field_email_address")}
                placeholder={t("field_business_email_placeholder")}
                {...register("email")}
                errorMessage={
                  errors?.email?.message ? String(errors.email?.message) : null
                }
                className="w-full"
              />
            </Box>
          </Box>

          <Button
            isLoading={forgotPasswordMutation.isPending}
            btnType={BtnType.Raised}
            sx={{ textTransform: "none", my: "32px" }}
            type="submit"
            className={cn(classes.btnAuthForm, "forgot-password-submit-button")}
          >
            <p className="m-0" translation-key="forgot_password_btn_send">
              {t("forgot_password_btn_send")}
            </p>
          </Button>

          <Link
            href={APP_ROUTES(locale).LOGIN.to}
            className="underline text-[14px] text-cimigoBlue text-center forgot-password-back-to-login"
            translation-key="forgot_password_back_to_login"
          >
            {t("forgot_password_back_to_login")}
          </Link>
        </Box>
      </form>
    </Box>
  );
}
